import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@web/components/ui/accordion";
import { Button } from "@web/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@web/components/ui/card";
import { useRefsContext } from "@web/lib/contexts/refs-context";
import { pageTitle } from "@web/lib/utils";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Award,
  BarChart3,
  Package,
  QrCode,
  Shield,
  Wine,
} from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";

export default function LandingPage() {
  pageTitle("Seja bem-vindo(a)");

  const { resourcesLink, worksLink, faqLink, moveToSectionsLink } =
    useRefsContext();

  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.replace("#", ""));
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, []);

  return (
    <main className="flex-1 [&>section]:flex [&>section]:justify-center [&>section]:px-4">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24 bg-gradient-to-br from-blue-950 via-cyan-950 to-indigo-950">
        <div
          className="absolute top-0 left-0 h-full w-full opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%23ffffff' fillOpacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <div className="container relative z-10 flex flex-col items-center text-center text-white">
          <motion.h2
            initial={{ opacity: 0, y: -50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mb-4 text-lg text-amber-200"
          >
            Sistema de Gestão de Selos
          </motion.h2>

          <motion.h1
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-6 font-bold text-4xl tracking-tight md:text-5xl lg:text-6xl"
          >
            Controle Total dos seus <br className="hidden sm:inline" />
            <span className="text-amber-300">Selos</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mb-8 max-w-[42rem] text-amber-100 md:text-xl"
          >
            Gerencie os{" "}
            <span className="font-semibold text-white">
              selos de autenticidade do seu produto
            </span>{" "}
            com numeração sequencial. Cada produto possui seu selo único com{" "}
            <span className="font-semibold text-white">
              controle de lotes numerados.
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col gap-4 sm:flex-row"
          >
            <Button
              size="lg"
              className="bg-blue-900 hover:bg-amber-700 text-white"
              asChild
            >
              <Link to="/tutorial">Começar Agora</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-amber-300 text-amber-300 hover:bg-amber-300 hover:text-amber-900 bg-transparent"
              asChild
            >
              <Link to="#" onClick={() => moveToSectionsLink(worksLink)}>
                Como Funciona
              </Link>
            </Button>
          </motion.div>
        </div>

        {/* Background Elements */}
        <div className="-z-10 absolute top-0 left-0 h-full w-full">
          <div className="-translate-y-1/2 absolute top-0 right-0 h-96 w-96 translate-x-1/2 rounded-full bg-amber-400/20 blur-3xl" />
          <div className="-translate-x-1/2 absolute bottom-0 left-0 h-96 w-96 translate-y-1/2 rounded-full bg-orange-400/20 blur-3xl" />
        </div>
      </section>

      {/* Features Section */}
      <section
        ref={resourcesLink}
        className="py-16 md:py-24 bg-gradient-to-br from-amber-50 to-orange-50"
      >
        <div className="container">
          <div className="mx-auto mb-12 max-w-[58rem] text-center">
            <motion.h2
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="mb-4 font-bold text-3xl md:text-4xl text-gray-900"
            >
              Recursos Completos para{" "}
              <span className="text-blue-900">Gestão de Selos</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="text-gray-600 md:text-xl"
            >
              Nossa plataforma oferece controle total sobre os{" "}
              <span className="font-semibold text-gray-900">
                selos de autenticidade do seu produto
              </span>{" "}
              com numeração sequencial e rastreabilidade completa.
            </motion.p>
          </div>

          <motion.div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: <Award className="h-8 w-8 text-white" />,
                title: "Selos de Autenticidade",
                description:
                  "Cada produto possui seu selo único com numeração sequencial para garantir autenticidade.",
                content:
                  "Crie selos personalizados para cada produto com numeração específica. Cada selo é único e rastreável, garantindo a autenticidade do produto.",
              },
              {
                icon: <Package className="h-8 w-8 text-white" />,
                title: "Lotes de Selos",
                description:
                  "Organize selos em lotes numerados (ex: 1-1000) para controle total da produção.",
                content:
                  "Associe numerações específicas aos selos de cada produto. Por exemplo, selos 1 a 1000 para um Produto X, controlando cada unidade produzida.",
              },
              {
                icon: <QrCode className="h-8 w-8 text-white" />,
                title: "Numeração Sequencial",
                description:
                  "Sistema inteligente de numeração sequencial para cada lote de selos produzido.",
                content:
                  "Numeração automática e sequencial dos selos, evitando duplicatas e garantindo controle total sobre cada selo produzido.",
              },
              {
                icon: <Wine className="h-8 w-8 text-white" />,
                title: "Vinculação com Produtos",
                description:
                  "Cada selo está vinculado a um produto específico com todas as informações detalhadas.",
                content:
                  "Mantenha a relação entre selos e produtos, com informações completas do produto: produtor, região, ano, tipo e características.",
              },
              {
                icon: <BarChart3 className="h-8 w-8 text-white" />,
                title: "Controle de Utilização",
                description:
                  "Acompanhe quantos selos foram utilizados e quantos ainda estão disponíveis.",
                content:
                  "Dashboard em tempo real mostrando selos utilizados vs disponíveis, permitindo controle preciso da produção e estoque.",
              },
              {
                icon: <Shield className="h-8 w-8 text-white" />,
                title: "Rastreabilidade Total",
                description:
                  "Histórico completo de cada selo desde a criação até a utilização final.",
                content:
                  "Rastreie cada selo individualmente com histórico completo de movimentações, datas e responsáveis por cada operação.",
              },
            ].map(({ icon, title, description, content }, index) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-lg">
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-900">
                        {icon}
                      </div>
                      {title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-2 text-gray-600 text-sm">
                    <p className="font-medium">{description}</p>
                    <p>{content}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Como Funciona Section */}
      <section className="bg-white py-16 md:py-24" ref={worksLink}>
        <div className="container">
          <div className="mx-auto mb-12 max-w-[58rem] text-center">
            <motion.h2
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mb-4 font-bold text-3xl md:text-4xl text-gray-900"
            >
              Como Funciona o Sistema de Selos
            </motion.h2>
            <p className="text-gray-600 md:text-xl">
              Um processo simples para criar, numerar e controlar os selos dos
              seus produtos.
            </p>
          </div>

          <div className="mx-auto max-w-5xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="flex flex-col items-center"
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-900 text-white">
                  <span className="font-bold text-xl">1</span>
                </div>
                <Card className="mt-4 w-full max-w-md">
                  <CardHeader>
                    <CardTitle>Cadastre o Produto</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">
                      Primeiro, cadastre o produto que receberá o selo com todas
                      as informações: nome, produtor, região, ano, tipo e
                      características.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                viewport={{ once: true }}
                className="flex flex-col items-center md:mt-32"
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-900 text-white">
                  <span className="font-bold text-xl">2</span>
                </div>
                <Card className="mt-4 w-full max-w-md">
                  <CardHeader>
                    <CardTitle>Crie o Lote de Selos</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">
                      Defina quantos selos serão produzidos para este produto
                      (ex: 1000 selos) e o sistema criará a numeração sequencial
                      automaticamente.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                viewport={{ once: true }}
                className="flex flex-col items-center"
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-900 text-white">
                  <span className="font-bold text-xl">3</span>
                </div>
                <Card className="mt-4 w-full max-w-md">
                  <CardHeader>
                    <CardTitle>Numeração Automática</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">
                      O sistema gera automaticamente a numeração sequencial (1,
                      2, 3... até 1000) para cada selo do lote, garantindo
                      unicidade.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.1 }}
                viewport={{ once: true }}
                className="flex flex-col items-center md:mt-32"
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-900 text-white">
                  <span className="font-bold text-xl">4</span>
                </div>
                <Card className="mt-4 w-full max-w-md">
                  <CardHeader>
                    <CardTitle>Controle de Utilização</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">
                      Acompanhe em tempo real quantos selos foram utilizados nos
                      produtos e quantos ainda estão disponíveis no lote.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.4 }}
              viewport={{ once: true }}
              className="mt-16 text-center"
            >
              <Button
                size="lg"
                className="bg-blue-900 hover:bg-amber-700"
                asChild
              >
                <Link to="/">
                  Começar Agora
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        className="py-16 md:py-24 bg-gradient-to-br from-amber-50 to-orange-50"
        ref={faqLink}
      >
        <div className="container">
          <div className="mx-auto mb-12 max-w-[58rem] text-center">
            <motion.h2
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="mb-4 font-bold text-3xl md:text-4xl text-gray-900"
            >
              Perguntas Frequentes
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-gray-600 md:text-xl"
            >
              Encontre respostas sobre o sistema de gestão de selos para
              produtos.
            </motion.p>
          </div>

          <div className="mx-auto max-w-3xl">
            <Accordion type="single" className="w-full">
              {[
                {
                  title: "O que são os selos e como funcionam?",
                  content:
                    "Os selos são etiquetas de autenticidade aplicadas em cada produto. Cada selo possui uma numeração única e sequencial (ex: 1, 2, 3... até 1000) que permite rastrear individualmente cada produto produzido. Isso garante autenticidade e controle total da produção.",
                },
                {
                  title: "Como é feita a numeração dos selos?",
                  content:
                    "A numeração é feita automaticamente pelo sistema de forma sequencial. Por exemplo, se você criar um lote de 1000 selos para um Produto X, o sistema gerará automaticamente os números de 1 a 1000, garantindo que não haja duplicatas ou números perdidos.",
                },
                {
                  title:
                    "Posso criar diferentes lotes de selos para o mesmo produto?",
                  content:
                    "Sim! Você pode criar múltiplos lotes para o mesmo produto. Por exemplo, um primeiro lote com selos 1-1000 e depois um segundo lote com selos 1001-2000. Cada lote é independente e pode ser controlado separadamente.",
                },
                {
                  title: "Como controlo quantos selos foram utilizados?",
                  content:
                    "O sistema permite registrar quando um selo é aplicado a um produto, atualizando automaticamente o contador de selos utilizados vs disponíveis. Você sempre saberá quantos selos restam em cada lote e pode acompanhar o progresso da produção.",
                },
                {
                  title: "Os selos podem ser rastreados individualmente?",
                  content:
                    "Sim! Cada selo possui um número único que permite rastreamento individual. Você pode consultar o histórico de qualquer selo específico, saber quando foi criado, quando foi utilizado e em qual produto foi aplicado.",
                },
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: -20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <AccordionItem value={`index-${index}`}>
                    <AccordionTrigger className="text-left">
                      {item.title}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      {item.content}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </main>
  );
}
